import { useState } from 'react'
import { motion } from 'framer-motion'
import { Terminal, BookOpen, FileText } from 'lucide-react'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'

type Tab = 'playground' | 'sdk' | 'logs'

export default function Developer() {
  const [activeTab, setActiveTab] = useState<Tab>('playground')
  const breadcrumbs = buildBreadcrumbs('/settings/integrations/developer')

  const tabs = [
    { id: 'playground' as Tab, label: 'API Playground', icon: Terminal },
    { id: 'sdk' as Tab, label: 'SDK & Docs', icon: BookOpen },
    { id: 'logs' as Tab, label: 'Request Logs', icon: FileText },
  ]

  return (
    <div>
      <PageHeader
        title="Developer Console"
        description="Test APIs, explore SDK documentation, and monitor request activity."
        breadcrumbs={breadcrumbs}
      />

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-warm-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-copper-500 text-copper-700'
                : 'border-transparent text-warm-500 hover:text-warm-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl">
        {activeTab === 'playground' && (
          <div className="rounded-xl border border-warm-200 bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                <Terminal className="w-5 h-5 text-copper-500" />
              </div>
              <h2 className="font-heading font-semibold text-midnight-800">API Playground</h2>
            </div>
            <p className="text-sm text-warm-500 mb-4">
              Test Personic AI API endpoints directly from your browser with your tenant credentials.
            </p>
            <div className="rounded-lg bg-midnight-900 p-4 font-mono text-sm text-warm-200">
              <span className="text-copper-400">POST</span> /v3/distill
              <br />
              <span className="text-warm-500">Content-Type: application/json</span>
              <br />
              <span className="text-warm-500">X-API-Key: your-api-key</span>
            </div>
          </div>
        )}

        {activeTab === 'sdk' && (
          <div className="rounded-xl border border-warm-200 bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-copper-500" />
              </div>
              <h2 className="font-heading font-semibold text-midnight-800">SDK & Documentation</h2>
            </div>
            <p className="text-sm text-warm-500 mb-4">
              Integrate Personic AI into your applications using our SDK libraries.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Python', 'JavaScript', 'REST API'].map((sdk) => (
                <div key={sdk} className="p-3 rounded-lg bg-warm-50 border border-warm-200">
                  <p className="text-sm font-medium text-midnight-800">{sdk}</p>
                  <p className="text-xs text-warm-500 mt-1">View documentation and examples</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="rounded-xl border border-warm-200 bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-copper-500" />
              </div>
              <h2 className="font-heading font-semibold text-midnight-800">Request Logs</h2>
            </div>
            <p className="text-sm text-warm-500">
              Recent API request logs will appear here. Logs are retained for 30 days.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  )
}
